import React, { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ApiURl from "../../controllers/Api";
import logo from "../../images/logo.png";
import html2pdf from 'html2pdf.js';

const Receipt = () => {
  const { order_id } = useParams();
  const [orderDetails, setOrderDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Horizontal scroll target for the table
  const tableScrollRef = useRef(null);

  // Hooks must be unconditional
  const statusBadge = useMemo(() => {
    const s = String(orderDetails?.status || "").toLowerCase();
    if (s === "completed") return "bg-[var(--user-gold,#D4AF37)]/12 text-[var(--user-maroon,#2B0000)] ring-1 ring-[var(--user-gold,#D4AF37)]/30";
    if (s === "pending") return "bg-[var(--user-gold,#D4AF37)]/12 text-[var(--user-maroon,#2B0000)] ring-1 ring-[var(--user-gold,#D4AF37)]/30";
    if (s === "cancelled") return "bg-[var(--user-maroon,#2B0000)]/8 text-[var(--user-maroon,#2B0000)] ring-1 ring-[var(--user-maroon,#2B0000)]/15";
    return "bg-[#F6EFEA] text-[var(--user-maroon,#2B0000)]/75 ring-1 ring-gray-200";
  }, [orderDetails?.status]);

  useEffect(() => {
    const fetchOrderDetails = async () => {
      try {
        const user_id = localStorage.getItem("user_id");

        if (!user_id) {
          setError("User ID is missing. Please log in.");
          setLoading(false);
          return;
        }

        const response = await fetch(
          `${ApiURl}/getOrderDetails.php?order_id=${order_id}&user_id=${user_id}`
        );

        if (!response.ok) throw new Error("Failed to fetch order details");

        const data = await response.json();
        if (data.success) {
          setOrderDetails(data.data[0]);
        } else {
          throw new Error(data.message || "No data available");
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [order_id]);

  // Function to resolve coupon discount - exactly like ViewDetails
  const resolveCouponDiscount = (info, subtotal) => {
    if (!info || subtotal <= 0) return 0;

    const couponId = Number(info?.coupon_id || 0);
    const hasCouponCode =
      info?.coupon_code && String(info.coupon_code).trim() !== "" && info?.coupon_code !== "N/A";

    if (!couponId && !hasCouponCode) return 0;

    const couponType = String(
      info?.coupon_type || info?.discount_type || ""
    ).toLowerCase();

    const fixedCandidates = [
      info?.coupon_value,
      info?.coupon_amount,
      info?.discount_amount,
      info?.discount_value,
      info?.applied_discount,
      info?.flat_discount,
    ];

    const percentageCandidates = [
      info?.coupon_percentage,
      info?.discount_percentage,
      info?.percentage,
    ];

    const fallbackCandidates = [info?.coupon_discount];

    const firstValid = (arr) => {
      for (const val of arr) {
        const num = Number(val);
        if (!Number.isNaN(num) && num > 0) return num;
      }
      return 0;
    };

    const fixedValue = firstValid(fixedCandidates);
    const percentageValue = firstValid(percentageCandidates);
    const fallbackValue = firstValid(fallbackCandidates);

    let discount = 0;

    if (couponType.includes("percent")) {
      discount = (subtotal * percentageValue) / 100;
    } else if (couponType.includes("fixed") || couponType.includes("flat")) {
      discount = fixedValue || fallbackValue;
    } else {
      discount = fixedValue || (percentageValue ? (subtotal * percentageValue) / 100 : fallbackValue);
    }

    return Math.min(subtotal, Number(discount || 0));
  };

  // Calculate price breakdown with discount hidden from display
  const priceBreakdown = useMemo(() => {
    const empty = {
      subtotal: 0,
      discountAmount: 0,
      discountedSubtotal: 0,
      totalCgst: 0,
      totalSgst: 0,
      totalIgst: 0,
      totalTax: 0,
      grandTotalBeforeDiscount: 0,
      grandTotalAfterDiscount: 0,
      roundedTotal: 0,
      roundingDiff: 0,
      itemWiseBreakdown: [],
    };

    if (!orderDetails || !Array.isArray(orderDetails.products)) return empty;

    const products = orderDetails.products;

    const safeProducts = products.map((item) => {
      const quantity = Number(item.quantity || 0);
      const price = Number(item.price || 0);
      const itemSubtotal = price * quantity;

      const cgst = Number(item.cgst || 0);
      const sgst = Number(item.sgst || 0);
      const igst = Number(item.igst || 0);

      const cgstRate = itemSubtotal > 0 ? cgst / itemSubtotal : 0;
      const sgstRate = itemSubtotal > 0 ? sgst / itemSubtotal : 0;
      const igstRate = itemSubtotal > 0 ? igst / itemSubtotal : 0;

      return {
        ...item,
        quantity,
        price,
        itemSubtotal,
        cgst,
        sgst,
        igst,
        cgstRate,
        sgstRate,
        igstRate,
      };
    });

    const subtotal = safeProducts.reduce((sum, item) => sum + item.itemSubtotal, 0);
    const discountAmount = resolveCouponDiscount(orderDetails, subtotal);
    const discountedSubtotal = Math.max(0, subtotal - discountAmount);

    let totalCgst = 0;
    let totalSgst = 0;
    let totalIgst = 0;

    const itemWiseBreakdown = safeProducts.map((item) => {
      const discountShare = subtotal > 0 ? (item.itemSubtotal / subtotal) * discountAmount : 0;
      const taxableAfterDiscount = Math.max(0, item.itemSubtotal - discountShare);

      const recalculatedCgst = taxableAfterDiscount * item.cgstRate;
      const recalculatedSgst = taxableAfterDiscount * item.sgstRate;
      const recalculatedIgst = taxableAfterDiscount * item.igstRate;

      totalCgst += recalculatedCgst;
      totalSgst += recalculatedSgst;
      totalIgst += recalculatedIgst;

      // Calculate discounted unit price
      const discountedUnitPrice = (item.itemSubtotal - discountShare) / item.quantity;

      return {
        name: item.name || "Unknown",
        product_id: item.product_id,
        size: item.size || "N/A",
        quantity: item.quantity,
        // Use discounted price for display (no discount shown)
        unitPrice: Number(discountedUnitPrice.toFixed(2)),
        // Use discounted subtotal for display
        total_amount: Number(taxableAfterDiscount.toFixed(2)),
        // Tax calculations
        cgst: Number(recalculatedCgst.toFixed(2)),
        sgst: Number(recalculatedSgst.toFixed(2)),
        igst: Number(recalculatedIgst.toFixed(2)),
        // Final line total
        grandtotal: Number(
          (taxableAfterDiscount + recalculatedCgst + recalculatedSgst + recalculatedIgst).toFixed(2)
        ),
      };
    });

    const totalTax = totalCgst + totalSgst + totalIgst;
    const grandTotalAfterDiscount = discountedSubtotal + totalTax;

    const roundedTotal = Math.round(grandTotalAfterDiscount);
    const roundingDiff = roundedTotal - grandTotalAfterDiscount;

    return {
      subtotal: Number(subtotal.toFixed(2)),
      discountAmount: Number(discountAmount.toFixed(2)),
      discountedSubtotal: Number(discountedSubtotal.toFixed(2)),
      totalCgst: Number(totalCgst.toFixed(2)),
      totalSgst: Number(totalSgst.toFixed(2)),
      totalIgst: Number(totalIgst.toFixed(2)),
      totalTax: Number(totalTax.toFixed(2)),
      grandTotalAfterDiscount: Number(grandTotalAfterDiscount.toFixed(2)),
      roundedTotal: Number(roundedTotal.toFixed(2)),
      roundingDiff: Number(roundingDiff.toFixed(2)),
      itemWiseBreakdown,
    };
  }, [orderDetails]);

  const hasDiscount = useMemo(() => {
    return priceBreakdown.discountAmount > 0;
  }, [priceBreakdown.discountAmount]);

  const finalAmount = useMemo(() => {
    return Number(priceBreakdown.roundedTotal || 0);
  }, [priceBreakdown.roundedTotal]);

  const handleDownloadPDF = () => {
    const element = document.getElementById("receipt-content");
    const opt = {
      margin: [0.5, 0.5, 0.5, 0.5],
      filename: `Receipt_${order_id}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { 
        scale: 2, 
        letterRendering: true,
        useCORS: true,
        logging: true 
      },
      jsPDF: { 
        unit: 'in', 
        format: 'a4', 
        orientation: 'portrait' 
      }
    };
    
    // Add header information to PDF
    const headerElement = document.createElement('div');
    headerElement.innerHTML = `
      <div style="text-align: center; margin-bottom: 20px; padding: 20px;">
        <h1 style="color: #333; font-size: 24px; margin-bottom: 10px;">Tax Invoice</h1>
        <p style="color: #666; font-size: 14px;">Order #${order_id} | Date: ${orderDetails?.date ? new Date(orderDetails.date).toLocaleDateString('en-IN', { 
          day: 'numeric', 
          month: 'short', 
          year: 'numeric' 
        }) : "-"}</p>
        <p style="color: #666; font-size: 14px;">Status: ${orderDetails?.status || '-'}</p>
        <hr style="margin: 20px 0; border: none; border-top: 1px solid #eee;" />
      </div>
    `;
    
    const receiptClone = element.cloneNode(true);
    receiptClone.insertBefore(headerElement, receiptClone.firstChild);
    
    html2pdf().set(opt).from(receiptClone).save();
  };

  const handlePrint = () => {
    const user_id = localStorage.getItem("user_id");
    if (!user_id) {
      setError("User ID is missing. Please log in.");
      return;
    }
    window.open(`${ApiURl}/download_invoice.php?invoice_number=${order_id}`, "_blank");
  };

  const scrollLeft = () => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (tableScrollRef.current) {
      tableScrollRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--user-bg,#E8D9D5)] p-2 sm:p-4 md:p-6 flex items-center justify-center">
        <div className="w-full max-w-2xl mx-2 sm:mx-4">
          <div className="bg-[#FBF7F2] rounded-xl sm:rounded-2xl md:rounded-[20px] border border-[var(--user-maroon,#2B0000)]/10 p-4 sm:p-6 md:p-8 shadow-[0_14px_40px_rgba(43,0,0,0.06)]">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 md:gap-6">
              <div className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 rounded-xl sm:rounded-2xl bg-[var(--user-gold,#D4AF37)]/15 animate-pulse" />
              <div className="flex-1 space-y-2 sm:space-y-3">
                <div className="h-4 sm:h-5 w-36 sm:w-44 md:w-48 bg-[var(--user-maroon,#2B0000)]/10 rounded-lg animate-pulse" />
                <div className="h-3 sm:h-4 w-48 sm:w-56 md:w-64 bg-[var(--user-maroon,#2B0000)]/10 rounded-lg animate-pulse" />
              </div>
            </div>
            <div className="mt-4 sm:mt-6 md:mt-8 space-y-3 sm:space-y-4">
              <div className="h-10 sm:h-12 md:h-14 rounded-xl sm:rounded-2xl bg-[#F6EFEA] animate-pulse" />
              <div className="h-10 sm:h-12 md:h-14 rounded-xl sm:rounded-2xl bg-[#F6EFEA] animate-pulse" />
              <div className="h-10 sm:h-12 md:h-14 rounded-xl sm:rounded-2xl bg-[#F6EFEA] animate-pulse" />
            </div>
            <p className="mt-4 sm:mt-6 text-center text-xs sm:text-sm text-[var(--user-maroon,#2B0000)]/45">Loading your receipt details...</p>
          </div>
        </div>
      </div>
    );
  }

  // Error State
  if (error) {
    return (
      <div className="min-h-screen bg-[var(--user-bg,#E8D9D5)] p-2 sm:p-4 md:p-6 flex items-center justify-center">
        <div className="w-full max-w-md mx-2">
          <div className="bg-[#FBF7F2] rounded-xl sm:rounded-2xl md:rounded-[20px] border border-[var(--user-maroon,#2B0000)]/10 p-5 sm:p-6 md:p-8 shadow-[0_14px_40px_rgba(43,0,0,0.06)] text-center">
            <div className="w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 mx-auto bg-red-100 rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4">
              <svg className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-[var(--user-maroon,#2B0000)] mb-1 sm:mb-2">Oops! Something went wrong</h3>
            <p className="text-xs sm:text-sm text-red-600 mb-4 sm:mb-6">{error}</p>
            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-center">
              <button
                onClick={() => window.location.reload()}
                className="px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 bg-[var(--user-maroon,#2B0000)] text-[var(--user-gold,#D4AF37)] font-semibold rounded-lg sm:rounded-xl text-xs sm:text-sm hover:opacity-90 transition-all shadow-md"
              >
                Try Again
              </button>
              <button
                onClick={() => navigate("/admin")}
                className="px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 bg-[#FBF7F2] border-2 border-[var(--user-maroon,#2B0000)]/10 text-[var(--user-maroon,#2B0000)]/75 font-semibold rounded-lg sm:rounded-xl text-xs sm:text-sm hover:bg-[#F6EFEA] transition-all"
              >
                Back to Dashboard
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // No Data State
  if (!orderDetails) {
    return (
      <div className="min-h-screen bg-[var(--user-bg,#E8D9D5)] p-2 sm:p-4 md:p-6 flex items-center justify-center">
        <div className="w-full max-w-md mx-2">
          <div className="bg-[#FBF7F2] rounded-xl sm:rounded-2xl md:rounded-[20px] border border-[var(--user-maroon,#2B0000)]/10 p-5 sm:p-6 md:p-8 shadow-[0_14px_40px_rgba(43,0,0,0.06)] text-center">
            <div className="w-16 h-16 sm:w-18 sm:h-18 md:w-20 md:h-20 mx-auto bg-[#F6EFEA] rounded-xl sm:rounded-2xl flex items-center justify-center mb-3 sm:mb-4">
              <svg className="w-8 h-8 sm:w-9 sm:h-9 md:w-10 md:h-10 text-[var(--user-maroon,#2B0000)]/35" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <h3 className="text-lg sm:text-xl font-bold text-[var(--user-maroon,#2B0000)] mb-1 sm:mb-2">No Receipt Found</h3>
            <p className="text-xs sm:text-sm text-[var(--user-maroon,#2B0000)]/60 mb-4 sm:mb-6">We couldn't find any receipt data for this order.</p>
            <button
              onClick={() => navigate("/admin")}
              className="px-4 sm:px-5 md:px-6 py-2 sm:py-2.5 md:py-3 bg-[var(--user-maroon,#2B0000)] text-[var(--user-gold,#D4AF37)] font-semibold rounded-lg sm:rounded-xl text-xs sm:text-sm hover:opacity-90 transition-all shadow-md"
            >
              Return to Dashboard
            </button>
          </div>
        </div>
      </div>
    );
  }

  const {
    order_id: orderId,
    date,
    status,
    address_line1,
    address_line2,
    landmark,
  } = orderDetails;

  const formattedAddress = [address_line1, address_line2, landmark].filter(Boolean).join(", ");

  return (
    <div className="min-h-screen bg-[var(--user-bg,#E8D9D5)] p-2 sm:p-4 lg:p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header Section */}
        <div className="mb-3 sm:mb-4 lg:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
            <div>
              <h1 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-medium tracking-[-0.02em] text-[var(--user-maroon,#2B0000)]">
                Tax Invoice
              </h1>
              <div className="mt-1 sm:mt-2 flex flex-wrap items-center gap-1.5 sm:gap-2 text-xs sm:text-sm text-[var(--user-maroon,#2B0000)]/60">
                <span className="font-medium">Order #{orderId}</span>
                <span className="w-1 h-1 rounded-full bg-gray-300 hidden sm:block" />
                <span className="text-xs sm:text-sm">{date ? new Date(date).toLocaleDateString('en-IN', { 
                  day: 'numeric', 
                  month: 'short', 
                  year: 'numeric' 
                }) : "-"}</span>
                <span className={`inline-flex items-center rounded-full px-2 sm:px-3 py-0.5 sm:py-1 text-[10px] sm:text-xs font-medium ${statusBadge}`}>
                  {status}
                </span>
              </div>
            </div>

            {/* Action Buttons - Hidden on mobile (shown in footer) */}
            <div className="hidden sm:flex flex-wrap gap-2">
              <button
                onClick={handlePrint}
                className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2.5 bg-[var(--user-maroon,#2B0000)] text-[var(--user-gold,#D4AF37)] font-semibold rounded-lg sm:rounded-xl text-xs sm:text-sm hover:opacity-90 transition-all shadow-md hover:shadow-lg active:scale-95"
              >
                 <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v2a2 2 0 002 2h14a2 2 0 002-2v-2M5 10h14l-7-7-7 7z" />
                </svg>
                Download
              </button>

              <button
                onClick={() => navigate("/admin")}
                className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2.5 bg-[#FBF7F2] border-2 border-[var(--user-maroon,#2B0000)]/10 text-[var(--user-maroon,#2B0000)]/75 font-semibold rounded-lg sm:rounded-xl text-xs sm:text-sm hover:bg-[#F6EFEA] hover:border-[var(--user-maroon,#2B0000)]/15 transition-all shadow-sm hover:shadow active:scale-95"
              >
                <svg className="w-3.5 h-3.5 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back
              </button>
            </div>
          </div>
        </div>

        {/* Main Receipt Card */}
        <div className="bg-[#FBF7F2] rounded-xl sm:rounded-2xl lg:rounded-[20px] border border-[var(--user-maroon,#2B0000)]/10 shadow-[0_14px_40px_rgba(43,0,0,0.06)] overflow-hidden">
          {/* Store Header */}
          <div className="p-3 sm:p-5 lg:p-6 border-b border-[var(--user-maroon,#2B0000)]/10">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6">
              <div className="flex items-center gap-2 sm:gap-3 lg:gap-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 bg-[var(--user-gold,#D4AF37)]/10 rounded-xl sm:rounded-2xl flex items-center justify-center p-2 sm:p-2.5 lg:p-3">
                  <img src={logo} alt="SL GOLD" className="w-full h-full object-contain" />
                </div>
                <div>
                  <h2 className="font-serif text-lg sm:text-xl lg:text-2xl font-medium text-[var(--user-maroon,#2B0000)]">SL GOLD</h2>
                  <p className="text-[10px] sm:text-xs lg:text-sm text-[var(--user-maroon,#2B0000)]/60 mt-0.5 sm:mt-1">Thank you for shopping with us!</p>
                </div>
              </div>

              {/* Total Amount Card */}
              <div className="bg-[var(--user-gold,#D4AF37)]/10 rounded-xl sm:rounded-2xl p-2.5 sm:p-3 lg:p-4 sm:min-w-[180px] border border-[var(--user-gold,#D4AF37)]/30">
                <p className="text-[8px] sm:text-[10px] lg:text-xs font-medium text-[var(--user-gold,#D4AF37)] uppercase tracking-wider">Total Amount</p>
                <p className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-bold text-[var(--user-maroon,#2B0000)] mt-0.5 sm:mt-1">
                  ₹{finalAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </p>
              </div>
            </div>

            {/* Info Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mt-4 sm:mt-5 lg:mt-6">
              {/* Order Details Card */}
              <div className="bg-[#F6EFEA] rounded-lg sm:rounded-xl p-3 sm:p-4 border border-[var(--user-maroon,#2B0000)]/10">
                <h3 className="text-[10px] sm:text-xs font-semibold text-[var(--user-maroon,#2B0000)]/45 uppercase tracking-wider mb-2 sm:mb-3">Order Details</h3>
                <div className="space-y-1.5 sm:space-y-2">
                  <div className="flex justify-between text-[10px] sm:text-xs">
                    <span className="text-[var(--user-maroon,#2B0000)]/60">Order ID</span>
                    <span className="font-medium text-[var(--user-maroon,#2B0000)]">#{orderId}</span>
                  </div>
                  <div className="flex justify-between text-[10px] sm:text-xs">
                    <span className="text-[var(--user-maroon,#2B0000)]/60">Date</span>
                    <span className="font-medium text-[var(--user-maroon,#2B0000)]">
                      {date ? new Date(date).toLocaleDateString('en-IN', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      }) : "-"}
                    </span>
                  </div>
                  <div className="flex justify-between text-[10px] sm:text-xs">
                    <span className="text-[var(--user-maroon,#2B0000)]/60">Status</span>
                    <span className={`inline-flex items-center rounded-full px-1.5 sm:px-2 py-0.5 text-[8px] sm:text-[10px] font-medium ${statusBadge}`}>
                      {status || "-"}
                    </span>
                  </div>
                </div>
              </div>

              {/* Shipping Address Card */}
              <div className="bg-[#F6EFEA] rounded-lg sm:rounded-xl p-3 sm:p-4 border border-[var(--user-maroon,#2B0000)]/10">
                <h3 className="text-[10px] sm:text-xs font-semibold text-[var(--user-maroon,#2B0000)]/45 uppercase tracking-wider mb-2 sm:mb-3">Shipping Address</h3>
                <p className="text-[10px] sm:text-xs text-[var(--user-maroon,#2B0000)]/85 leading-relaxed break-words">
                  {formattedAddress || "No address provided"}
                </p>
              </div>
            </div>
          </div>

          {/* Products Section */}
          <div className="p-3 sm:p-5 lg:p-6">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 sm:gap-3 lg:gap-4 mb-3 sm:mb-4">
              <div>
                <h3 className="font-serif text-base sm:text-lg lg:text-xl font-medium text-[var(--user-maroon,#2B0000)]">Products</h3>
                <p className="text-[10px] sm:text-xs text-[var(--user-maroon,#2B0000)]/60 mt-0.5 sm:mt-1">
                  {priceBreakdown.itemWiseBreakdown.length} item{priceBreakdown.itemWiseBreakdown.length === 1 ? "" : "s"} • 
                  <span className="ml-1 text-[var(--user-gold,#D4AF37)] font-medium">
                    ₹{priceBreakdown.grandTotalAfterDiscount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </p>
              </div>

              {/* Scroll Controls - Hidden on mobile, shown on larger screens */}
              {priceBreakdown.itemWiseBreakdown.length > 3 && (
                <div className="hidden sm:flex items-center gap-2">
                  <button
                    onClick={scrollLeft}
                    className="p-1.5 sm:p-2 bg-[#F6EFEA] rounded-lg sm:rounded-xl hover:bg-[var(--user-maroon,#2B0000)]/10 transition-all active:scale-95"
                    aria-label="Scroll left"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[var(--user-maroon,#2B0000)]/75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                    </svg>
                  </button>
                  <button
                    onClick={scrollRight}
                    className="p-1.5 sm:p-2 bg-[#F6EFEA] rounded-lg sm:rounded-xl hover:bg-[var(--user-maroon,#2B0000)]/10 transition-all active:scale-95"
                    aria-label="Scroll right"
                  >
                    <svg className="w-4 h-4 sm:w-5 sm:h-5 text-[var(--user-maroon,#2B0000)]/75" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                    </svg>
                  </button>
                </div>
              )}
            </div>

            {/* Products Table */}
            <div
              id="receipt-content"
              ref={tableScrollRef}
              className="overflow-x-auto rounded-lg sm:rounded-xl border border-[var(--user-maroon,#2B0000)]/10 bg-[#FBF7F2] scroll-smooth"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "#cbd5e1 #f1f5f9",
                WebkitOverflowScrolling: "touch",
              }}
            >
              <table className="min-w-[900px] sm:min-w-[1000px] w-full table-auto">
                <thead>
                  <tr className="bg-[var(--user-maroon,#2B0000)] border-b border-[var(--user-maroon,#2B0000)]/10">
                    <th className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-left text-[8px] sm:text-xs font-semibold text-[var(--user-gold,#D4AF37)] uppercase tracking-wider w-1/4">Product</th>
                    <th className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-center text-[8px] sm:text-xs font-semibold text-[var(--user-gold,#D4AF37)] uppercase tracking-wider w-1/12">Qty</th>
                    <th className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-center text-[8px] sm:text-xs font-semibold text-[var(--user-gold,#D4AF37)] uppercase tracking-wider w-1/12">Size</th>
                    <th className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-right text-[8px] sm:text-xs font-semibold text-[var(--user-gold,#D4AF37)] uppercase tracking-wider w-1/12">Price</th>
                    <th className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-right text-[8px] sm:text-xs font-semibold text-[var(--user-gold,#D4AF37)] uppercase tracking-wider w-1/12">Total</th>
                    <th className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-right text-[8px] sm:text-xs font-semibold text-[var(--user-gold,#D4AF37)] uppercase tracking-wider w-1/12">IGST</th>
                    <th className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-right text-[8px] sm:text-xs font-semibold text-[var(--user-gold,#D4AF37)] uppercase tracking-wider w-1/12">SGST</th>
                    <th className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-right text-[8px] sm:text-xs font-semibold text-[var(--user-gold,#D4AF37)] uppercase tracking-wider w-1/12">CGST</th>
                    <th className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-right text-[8px] sm:text-xs font-semibold text-[var(--user-gold,#D4AF37)] uppercase tracking-wider w-1/12">Grand Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[var(--user-maroon,#2B0000)]/10">
                  {priceBreakdown.itemWiseBreakdown.length > 0 ? (
                    priceBreakdown.itemWiseBreakdown.map((product, index) => (
                      <tr key={index} className="hover:bg-[#F6EFEA] transition-colors">
                        <td className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3">
                          <div className="text-[10px] sm:text-xs font-medium text-[var(--user-maroon,#2B0000)] break-words pr-1">
                            {product.name}
                          </div>
                        </td>
                        <td className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-center text-[10px] sm:text-xs text-[var(--user-maroon,#2B0000)]">{product.quantity}</td>
                        <td className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-center text-[10px] sm:text-xs text-[var(--user-maroon,#2B0000)]/60">{product.size || '-'}</td>
                        <td className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-mono text-[var(--user-maroon,#2B0000)] whitespace-nowrap">
                          ₹{(product.unitPrice || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-mono text-[var(--user-maroon,#2B0000)] whitespace-nowrap">
                          ₹{(product.total_amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-mono text-[var(--user-maroon,#2B0000)] whitespace-nowrap">
                          ₹{(product.igst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-mono text-[var(--user-maroon,#2B0000)] whitespace-nowrap">
                          ₹{(product.sgst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-mono text-[var(--user-maroon,#2B0000)] whitespace-nowrap">
                          ₹{(product.cgst || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                        <td className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-mono font-semibold text-[var(--user-maroon,#2B0000)] whitespace-nowrap">
                          ₹{(product.grandtotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan={9} className="px-2 sm:px-3 lg:px-4 py-4 sm:py-6 lg:py-8 text-center text-xs sm:text-sm text-[var(--user-maroon,#2B0000)]/45">
                        No products available for this order.
                      </td>
                    </tr>
                  )}
                </tbody>
                <tfoot className="bg-[#F6EFEA] border-t-2 border-[var(--user-maroon,#2B0000)]/10">
                  <tr>
                    <td colSpan={8} className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-semibold text-[var(--user-maroon,#2B0000)]/75">
                      Grand Total:
                    </td>
                    <td className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-mono font-bold text-[var(--user-maroon,#2B0000)] whitespace-nowrap">
                      ₹{priceBreakdown.grandTotalAfterDiscount.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                  <tr className="border-t border-[var(--user-maroon,#2B0000)]/10">
                    <td colSpan={8} className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-right text-[8px] sm:text-[10px] text-[var(--user-maroon,#2B0000)]/60">
                      Rounding Difference:
                    </td>
                    <td className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-right text-[8px] sm:text-[10px] font-mono text-[var(--user-maroon,#2B0000)]/75 whitespace-nowrap">
                      ₹{priceBreakdown.roundingDiff.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                  <tr className="bg-[var(--user-gold,#D4AF37)]/10">
                    <td colSpan={8} className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-semibold text-[var(--user-gold,#D4AF37)]">
                      Round Off Amount:
                    </td>
                    <td className="px-2 sm:px-3 lg:px-4 py-2 sm:py-3 text-right text-[10px] sm:text-xs font-mono font-bold text-[var(--user-gold,#D4AF37)] whitespace-nowrap">
                      ₹{priceBreakdown.roundedTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* Mobile Scroll Hint */}
            {priceBreakdown.itemWiseBreakdown.length > 3 && (
              <div className="mt-2 sm:mt-3 text-[10px] sm:text-xs text-[var(--user-maroon,#2B0000)]/45 flex items-center justify-center gap-1 sm:hidden">
                <svg className="w-3 h-3 sm:w-4 sm:h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
                <span>Swipe to see all columns</span>
              </div>
            )}
          </div>
        </div>

        {/* Mobile Bottom Actions */}
        <div className="mt-3 sm:mt-4 grid grid-cols-3 gap-2 sm:hidden">
          <button
            onClick={handlePrint}
            className="flex items-center justify-center gap-1 py-2 bg-[var(--user-maroon,#2B0000)] text-[var(--user-gold,#D4AF37)] font-semibold rounded-lg text-[10px]"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            Print
          </button>
          <button
            onClick={handleDownloadPDF}
            className="flex items-center justify-center gap-1 py-2 bg-[var(--user-gold,#D4AF37)] text-[var(--user-gold,#D4AF37)] font-semibold rounded-lg text-[10px]"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3M3 17v2a2 2 0 002 2h14a2 2 0 002-2v-2M5 10h14l-7-7-7 7z" />
            </svg>
            PDF
          </button>
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center justify-center gap-1 py-2 bg-[#FBF7F2] border-2 border-[var(--user-maroon,#2B0000)]/10 text-[var(--user-maroon,#2B0000)]/75 font-semibold rounded-lg text-[10px]"
          >
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
            </svg>
            Back
          </button>
        </div>
      </div>
    </div>
  );
};

export default Receipt;